import { ColDef, EditableCallbackParams, ICellEditorParams, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import { GridCheckBoxCell } from "src/components/common/form/checkbox/GridCheckBoxCell";
import { GridDropDownForm } from "src/components/common/form/dropdown_custom/GridDropDownForm";
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";
import { WorkPlaceDto } from "src/dtos/workplace/workPlaceDto";
import { GridDropDownCommitAfter } from "./GridHandlerContent";

// 사업장 목록 데이터 타입을 명확히 정의합니다.
interface CompCdCellParams {
    compCd: string;
    compNm: string;
}

interface CommonCodeCellParams {
    minorCd: string;
    codeNm: string;
}

type ChangeColumnContext<TData> = {
	data: TData;
	newValue: string;
	params: ICellEditorParams<TData> | ICellRendererParams<TData>;
};

interface CheckBoxColumnParams {
	checkedValue?: string;
	uncheckedValue?: string;
	label?: string;
	checkedLabel?: string;
	uncheckedLabel?: string;
}

type ChangeColumnOptions<TData> = {
	// 값 변경 전 화면별 조건을 검사합니다. false를 반환하면 행 업데이트를 중단합니다.
	canChange?: (context: ChangeColumnContext<TData>) => boolean;
	// 기본 변경값 외에 함께 초기화하거나 갱신할 필드를 반환합니다.
	getPatch?: (context: ChangeColumnContext<TData>) => Partial<TData>;
};

/**
 * AG-Grid '사업장' 컬럼을 위한 공통 옵션 객체를 반환하는 함수
 * @param createCellClass 동적 클래스 생성을 위한 함수
 * @param compCdList 사업장 목록 데이터
 * @returns ColDef 객체에 적용할 공통 옵션
 */
export const getCompCdColumnOptions = <TData extends { valueIndex: number }>(
    compCdList: CompCdCellParams[],
	editable: boolean | ((params: EditableCallbackParams<TData>) => boolean),
	onChange: ((newValue: string, params: ICellEditorParams<TData>) => void) | undefined,
	onCommitAfter: GridDropDownCommitAfter<TData> | undefined,
): Partial<ColDef<TData>> => {
	const optionList = compCdList.map(opt => ({
		value: opt.compCd,
		label: opt.compNm,
	}));

    return {
        editable: editable,
        cellEditor: GridDropDownForm as any,
        cellEditorPopup: true,
        cellEditorPopupPosition: "under",
        cellEditorParams: {
			optionList,
			onChange,
			onCommitAfter: onCommitAfter ?? undefined,
        } as any,
        valueFormatter: (params: ValueFormatterParams<TData>) => {
            const comp = compCdList.find(opt => opt.compCd === params.value);
            return comp ? comp.compNm : params.value;
        },
    };
};

export const changeCompCdColumn = <TData extends {
	valueIndex: number;
	compCd?: string;
	inserted?: boolean;
	isModified?: boolean;
}>(
	updateRow: ((rowIndex: number, updatedData: TData) => void) | undefined,
	options?: ChangeColumnOptions<TData>,
) => {
	if(!updateRow) return undefined;
	return (newValue: string, params: ICellEditorParams<TData>) => {
		const data = params.data;
		if (!data) return;
		if (data.compCd === newValue) return;

		const context = { data, newValue, params };
		if (options?.canChange && !options.canChange(context)) return;

		updateRow(data.valueIndex, {
			...data,
			compCd: newValue,
			isModified: data.inserted ? false : true,
			...options?.getPatch?.(context),
		});
	};
};

export const getCommonCodeColumnOptions = <TData extends { valueIndex: number }>(
    commonCodeList: CommonCodeCellParams[],
	editable: boolean | ((params: EditableCallbackParams<TData>) => boolean),
	onChange: ((newValue: string, params: ICellEditorParams<TData>) => void) | undefined,
	onCommitAfter: GridDropDownCommitAfter<TData> | undefined,
): Partial<ColDef<TData>> => {
	const optionList = commonCodeList.map(opt => ({
		value: opt.minorCd,
		label: opt.codeNm,
	}));

    return {
        editable: editable,
        cellEditor: GridDropDownForm as any,
        cellEditorPopup: true,
        cellEditorPopupPosition: "under",
        cellEditorParams: {
			optionList,
			onChange: onChange ?? undefined,
			onCommitAfter: onCommitAfter ?? undefined,
        } as any,
        valueFormatter: (params: ValueFormatterParams<TData>) => {
            const commonCode = commonCodeList.find(
				opt => opt.minorCd === params.value || String(opt.minorCd) === String(params.value)
			);
            return commonCode ? commonCode.codeNm : params.value;
        },
    };
};

export const changeCommonCodeColumn = <TData extends {
	valueIndex: number;
	inserted?: boolean;
	isModified?: boolean;
}>(
	updateRow: ((rowIndex: number, updatedData: TData) => void) | undefined,
	options?: ChangeColumnOptions<TData>,
) => {
	if (!updateRow) return undefined;
	return (newValue: string, params: ICellEditorParams<TData>) => {
		const data = params.data;
		const field = params.colDef.field as keyof TData | undefined;
		if (!data || !field) return;
		if (String(data[field] ?? '') === String(newValue)) return;
		const context = { data, newValue, params };
		if (options?.canChange && !options.canChange(context)) return;
		updateRow(data.valueIndex, {
		...data,
		[field]: newValue,
		isModified: data.inserted ? false : true,
		...options?.getPatch?.(context),
		} as TData);
	};
};

export const getWorkPlaceColumnOptions = <TData extends { valueIndex: number, workplaceCd?: string }>(
	workPlaceList: WorkPlaceDto[],
	editable: boolean | ((params: EditableCallbackParams<TData>) => boolean),
	compCd: ((params: any) => string),
	onChange: ((newValue: string, params: ICellEditorParams<TData>) => void) | undefined,
	onCommitAfter: GridDropDownCommitAfter<TData> | undefined,
): Partial<ColDef<TData>> => {
	return {
		editable: editable,
		cellEditor: GridDropDownForm as any,
		cellEditorPopup: true,
		cellEditorPopupPosition: "under",
		cellEditorParams: ((params: ICellEditorParams<TData>) => {
			const targetCompCd = compCd(params);
			const filteredList = workPlaceList.filter(item => item.compCd === targetCompCd);

			return {
				optionList: filteredList.map(opt => ({
					value: opt.workplaceCd,
					label: opt.workplaceNm,
				})),
				onChange: onChange ?? undefined,
				onCommitAfter: onCommitAfter ?? undefined,
			};
		}) as any,
		valueFormatter: (params: ValueFormatterParams<TData>) => {
			const workPlace = workPlaceList.find(
				opt => opt.workplaceCd === params.value || String(opt.workplaceCd) === String(params.value)
			);
			return workPlace ? workPlace.workplaceNm : params.value;
		},
	};
};

export const changeWorkplaceColumn = <TData extends {
	valueIndex: number;
	workplaceCd?: string;
	inserted?: boolean;
	isModified?: boolean;
}>(
	updateRow: ((rowIndex: number, updatedData: TData) => void) | undefined,
	options?: ChangeColumnOptions<TData>,
) => {
	if(!updateRow) return undefined;
	return (newValue: string, params: ICellEditorParams<TData>) => {
		const data = params.data;
		if (!data) return;
		if (data.workplaceCd === newValue) return;

		const context = { data, newValue, params };
		if (options?.canChange && !options.canChange(context)) return;

		updateRow(data.valueIndex, {
			...data,
			workplaceCd: newValue,
			isModified: data.inserted ? false : true,
			...options?.getPatch?.(context),
		});
	};
};

export const getWarehouseColumnOptions = <TData extends { valueIndex: number }>(
	warehouseList: WareHouseDto[],
	editable: boolean | ((params: EditableCallbackParams<TData>) => boolean),
	logicCenter: ((params: any) => string), // logicCenter 값을 추출하는 함수 - aggrid 때문에 함수로만 전달받음
	onChange: ((newValue: string, params: ICellEditorParams<TData>) => void) | undefined,
	onCommitAfter: GridDropDownCommitAfter<TData> | undefined,
): Partial<ColDef<TData>> => {

	return {
		editable: editable,
		cellEditor: GridDropDownForm as any,
		cellEditorPopup: true,
		cellEditorPopupPosition: "under",
		cellEditorParams: ((params: ICellEditorParams<TData>) => {
			const targetLogicCenter = logicCenter(params); 
			
			const filteredList = warehouseList.filter(item => item.logicCenter === targetLogicCenter);
			
			return {
				optionList: filteredList.map(opt => ({
					value: opt.warehouseCd,
					label: opt.warehouseNm,
				})),
				onChange: onChange ?? undefined,
				onCommitAfter: onCommitAfter ?? undefined,
			};
		}) as any,
		// Formatter는 전체 리스트를 기준으로 이름을 표시 (필터링된 목록에 없더라도 코드가 맞으면 이름을 보여주기 위함)
		valueFormatter: (params: ValueFormatterParams<TData>) => {
			const warehouse = warehouseList.find(
				opt => opt.warehouseCd === params.value || String(opt.warehouseCd) === String(params.value)
			);
			return warehouse ? warehouse.warehouseNm : params.value;
		},
	};
};

export const changeWarehouseColumn = <TData extends {
	valueIndex: number;
	warehouseCd?: string;
	inserted?: boolean;
	isModified?: boolean;
}>(
	updateRow: ((rowIndex: number, updatedData: TData) => void) | undefined,
	options?: ChangeColumnOptions<TData>,
) => {
	if(!updateRow) return undefined;
	return (newValue: string, params: ICellEditorParams<TData>) => {
		const data = params.data;
		if (!data) return;
		if (data.warehouseCd === newValue) return;

		const context = { data, newValue, params };
		if (options?.canChange && !options.canChange(context)) return;

		updateRow(data.valueIndex, {
			...data,
			warehouseCd: newValue,
			isModified: data.inserted ? false : true,
			...options?.getPatch?.(context),
		});
	};
};

export const getCheckBoxColumnOptions = <TData extends { valueIndex: number }>(
	editable: boolean | ((params: EditableCallbackParams<TData>) => boolean),
	onChange: ((newValue: string, params: ICellRendererParams<TData>) => void) | undefined,
	_onCommitAfter: GridDropDownCommitAfter<TData> | undefined,
	params?: CheckBoxColumnParams,
): Partial<ColDef<TData>> => {
	const checkedValue = params?.checkedValue ?? "Y";
	const uncheckedValue = params?.uncheckedValue ?? "N";

	return {
		editable: false,
		cellRenderer: (rendererParams: ICellRendererParams<TData>) => {
			const isEditable = typeof editable === "function"
				? editable(rendererParams as EditableCallbackParams<TData>)
				: editable;

			return (
				<GridCheckBoxCell
					{...rendererParams}
					checkedValue={checkedValue}
					uncheckedValue={uncheckedValue}
					onChange={onChange ?? undefined}
					editable={isEditable}
				/>
			);
		},
	};
};

export const changeCheckBoxColumn = <TData extends {
	valueIndex: number;
	inserted?: boolean;
	isModified?: boolean;
}>(
	updateRow: ((rowIndex: number, updatedData: TData) => void) | undefined,
	options?: ChangeColumnOptions<TData> & Pick<CheckBoxColumnParams, "checkedValue" | "uncheckedValue">,
) => {
	if (!updateRow) return undefined;

	const checkedValue = options?.checkedValue ?? "Y";
	const uncheckedValue = options?.uncheckedValue ?? "N";

	return (newValue: string, params: ICellRendererParams<TData>) => {
		const data = params.data;
		const field = params.colDef?.field as keyof TData | undefined;
		if (!data || !field) return;
		if (String(data[field] ?? "") === String(newValue)) return;

		const context = { data, newValue, params };
		if (options?.canChange && !options.canChange(context)) return;

		updateRow(data.valueIndex, {
			...data,
			[field]: newValue,
			isModified: data.inserted ? false : true,
			...options?.getPatch?.(context),
		} as TData);
	};
};

export const CompCdColumn = {
	options: getCompCdColumnOptions,
	change: changeCompCdColumn,
};

export const CommonCodeColumn = {
	options: getCommonCodeColumnOptions,
	change: changeCommonCodeColumn,
};

export const WorkPlaceColumn = {
	options: getWorkPlaceColumnOptions,
	change: changeWorkplaceColumn,
};

export const WarehouseColumn = {
	options: getWarehouseColumnOptions,
	change: changeWarehouseColumn,
};

export const CheckBoxColumn = {
	options: getCheckBoxColumnOptions,
	change: changeCheckBoxColumn,
};